import { initializeApp, getApps } from "firebase/app";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import api from "./api";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const hasFirebaseConfig = () =>
  Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId && vapidKey);

const getFirebaseApp = () => {
  if (!hasFirebaseConfig()) return null;
  return getApps()[0] || initializeApp(firebaseConfig);
};

const serviceWorkerUrl = () => {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey || "",
    authDomain: firebaseConfig.authDomain || "",
    projectId: firebaseConfig.projectId || "",
    storageBucket: firebaseConfig.storageBucket || "",
    messagingSenderId: firebaseConfig.messagingSenderId || "",
    appId: firebaseConfig.appId || "",
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
};

export async function registerPushNotifications({ requestPermission = false } = {}) {
  try {
    if (!localStorage.getItem("token") || !hasFirebaseConfig()) return null;
    if (!(await isSupported()) || !("serviceWorker" in navigator) || !("Notification" in window)) return null;

    let permission = Notification.permission;
    if (permission === "default" && requestPermission) permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const registration = await navigator.serviceWorker.register(serviceWorkerUrl());
    const app = getFirebaseApp();
    if (!app) return null;

    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    if (!token) return null;

    const oldToken = localStorage.getItem("fcmToken");
    if (oldToken !== token) {
      await api.post("/users/fcm-token", { token });
      localStorage.setItem("fcmToken", token);
    }

    return token;
  } catch (error) {
    console.warn("Push notification registration failed:", error?.message || error);
    return null;
  }
}

export async function removePushToken() {
  const storedToken = localStorage.getItem("fcmToken");
  try {
    if (storedToken && localStorage.getItem("token")) {
      await api.delete("/users/fcm-token", { data: { token: storedToken } });
    }

    if (await isSupported()) {
      const app = getFirebaseApp();
      if (app) await deleteToken(getMessaging(app));
    }
  } catch (error) {
    console.warn("Push notification cleanup failed:", error?.message || error);
  } finally {
    localStorage.removeItem("fcmToken");
  }
}

export async function listenForForegroundMessages(callback) {
  try {
    if (!hasFirebaseConfig() || !(await isSupported())) return () => {};
    const app = getFirebaseApp();
    if (!app) return () => {};
    return onMessage(getMessaging(app), (payload) => callback?.(payload));
  } catch {
    return () => {};
  }
}
